import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  UnorderedList,
  ListItem,
  Link
} from "@chakra-ui/react";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const PrivacyPolicy: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/privacy-policy"
        fallbackTitle="Privacy Policy"
        fallbackDescription="How we protect your information and privacy"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg="blue.700" color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={4} align="start">
            <Text fontSize="sm" color="blue.200" textTransform="uppercase" letterSpacing="wider">
              Legal Document
            </Text>
            <Heading size="2xl" fontWeight="bold">
              Privacy Policy
            </Heading>
            <Text fontSize="lg" color="blue.100">
              Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
            </Text>
            <Text fontSize="sm" color="blue.200" mt={2}>
              Last Updated: November 2025
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxW="container.lg" py={16}>
        <VStack spacing={8} align="stretch">
          {/* Introduction */}
          <Box>
            <Text fontSize="lg" color="gray.700" lineHeight="tall">
              <strong>[PLACEHOLDER CONTENT]</strong> One Group Pty Ltd (ABN XX XXX XXX XXX) and its related entities
              ("we", "us", "our") are committed to protecting your privacy in accordance with the Privacy Act 1988 (Cth)
              and the Australian Privacy Principles (APPs). This Privacy Policy describes how we handle your personal information.
            </Text>
          </Box>

          {/* Table of Contents */}
          <Box bg="blue.50" p={6} borderRadius="md" borderLeft="4px solid" borderColor="blue.500">
            <Heading size="sm" mb={4} color="blue.800">Table of Contents</Heading>
            <VStack align="start" spacing={2}>
              <Link color="blue.600" href="#section-1">1. Information We Collect</Link>
              <Link color="blue.600" href="#section-2">2. How We Use Your Information</Link>
              <Link color="blue.600" href="#section-3">3. Data Protection and Security</Link>
              <Link color="blue.600" href="#section-4">4. Cookies and Tracking</Link>
              <Link color="blue.600" href="#section-5">5. Third-Party Services</Link>
              <Link color="blue.600" href="#section-6">6. Your Privacy Rights</Link>
              <Link color="blue.600" href="#section-7">7. Contact Information</Link>
            </VStack>
          </Box>

          {/* Accordion Sections */}
          <Accordion allowMultiple defaultIndex={[0]}>
            <AccordionItem id="section-1" border="1px" borderColor="gray.200" borderRadius="md" mb={4}>
              <h2>
                <AccordionButton py={4} _hover={{ bg: "blue.50" }}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md" color="blue.800">1. Information We Collect</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={6} bg="gray.50">
                <VStack align="start" spacing={4}>
                  <Text color="gray.700">
                    <strong>[PLACEHOLDER]</strong> We collect various types of information to provide and improve our services:
                  </Text>
                  <Box>
                    <Heading size="sm" mb={2} color="gray.800">Personal Information:</Heading>
                    <UnorderedList spacing={2} color="gray.700">
                      <ListItem>Name, email address, phone number</ListItem>
                      <ListItem>Residential and business addresses</ListItem>
                      <ListItem>Date of birth and identification documents</ListItem>
                      <ListItem>Financial information (bank details, payment history)</ListItem>
                    </UnorderedList>
                  </Box>
                  <Box>
                    <Heading size="sm" mb={2} color="gray.800">Usage Information:</Heading>
                    <UnorderedList spacing={2} color="gray.700">
                      <ListItem>IP addresses and device identifiers</ListItem>
                      <ListItem>Browser type and operating system</ListItem>
                      <ListItem>Pages visited and time spent on our platform</ListItem>
                      <ListItem>Referral sources and clickstream data</ListItem>
                    </UnorderedList>
                  </Box>
                </VStack>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem id="section-2" border="1px" borderColor="gray.200" borderRadius="md" mb={4}>
              <h2>
                <AccordionButton py={4} _hover={{ bg: "blue.50" }}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md" color="blue.800">2. How We Use Your Information</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={6} bg="gray.50">
                <VStack align="start" spacing={4}>
                  <Text color="gray.700">
                    <strong>[PLACEHOLDER]</strong> We use your personal information for the following purposes:
                  </Text>
                  <UnorderedList spacing={2} color="gray.700">
                    <ListItem>To provide and maintain our business management services</ListItem>
                    <ListItem>To process transactions and manage client relationships</ListItem>
                    <ListItem>To communicate with you about our services, updates, and offers</ListItem>
                    <ListItem>To comply with legal obligations and regulatory requirements</ListItem>
                    <ListItem>To improve our platform and develop new features</ListItem>
                    <ListItem>To prevent fraud and ensure platform security</ListItem>
                    <ListItem>To analyze usage patterns and optimize user experience</ListItem>
                  </UnorderedList>
                </VStack>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem id="section-3" border="1px" borderColor="gray.200" borderRadius="md" mb={4}>
              <h2>
                <AccordionButton py={4} _hover={{ bg: "blue.50" }}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md" color="blue.800">3. Data Protection and Security</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={6} bg="gray.50">
                <VStack align="start" spacing={4}>
                  <Text color="gray.700">
                    <strong>[PLACEHOLDER]</strong> We implement industry-standard security measures to protect your personal information:
                  </Text>
                  <UnorderedList spacing={2} color="gray.700">
                    <ListItem>256-bit SSL encryption for data transmission</ListItem>
                    <ListItem>Secure cloud storage with regular backups</ListItem>
                    <ListItem>Multi-factor authentication for account access</ListItem>
                    <ListItem>Regular security audits and vulnerability assessments</ListItem>
                    <ListItem>Staff training on data protection and privacy practices</ListItem>
                    <ListItem>Restricted access to personal information on a need-to-know basis</ListItem>
                  </UnorderedList>
                  <Text color="gray.700" fontSize="sm" fontStyle="italic">
                    While we take reasonable precautions, no method of transmission over the internet is 100% secure.
                    We cannot guarantee absolute security of your information.
                  </Text>
                </VStack>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem id="section-4" border="1px" borderColor="gray.200" borderRadius="md" mb={4}>
              <h2>
                <AccordionButton py={4} _hover={{ bg: "blue.50" }}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md" color="blue.800">4. Cookies and Tracking Technologies</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={6} bg="gray.50">
                <VStack align="start" spacing={4}>
                  <Text color="gray.700">
                    <strong>[PLACEHOLDER]</strong> We use cookies and similar technologies to enhance your experience:
                  </Text>
                  <Box>
                    <Heading size="sm" mb={2} color="gray.800">Essential Cookies:</Heading>
                    <Text color="gray.700">Required for platform functionality, authentication, and security.</Text>
                  </Box>
                  <Box>
                    <Heading size="sm" mb={2} color="gray.800">Analytics Cookies:</Heading>
                    <Text color="gray.700">Help us understand how users interact with our platform to improve services.</Text>
                  </Box>
                  <Box>
                    <Heading size="sm" mb={2} color="gray.800">Preference Cookies:</Heading>
                    <Text color="gray.700">Remember your settings and preferences for a personalized experience.</Text>
                  </Box>
                  <Text color="gray.700" fontSize="sm">
                    You can manage cookie preferences through your browser settings. Note that disabling certain cookies
                    may affect platform functionality.
                  </Text>
                </VStack>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem id="section-5" border="1px" borderColor="gray.200" borderRadius="md" mb={4}>
              <h2>
                <AccordionButton py={4} _hover={{ bg: "blue.50" }}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md" color="blue.800">5. Third-Party Services and Disclosure</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={6} bg="gray.50">
                <VStack align="start" spacing={4}>
                  <Text color="gray.700">
                    <strong>[PLACEHOLDER]</strong> We may share your information with trusted third parties:
                  </Text>
                  <UnorderedList spacing={2} color="gray.700">
                    <ListItem>Payment processors (Stripe) for transaction processing</ListItem>
                    <ListItem>Email service providers for communications</ListItem>
                    <ListItem>Cloud hosting providers for data storage</ListItem>
                    <ListItem>Analytics services for platform improvement</ListItem>
                    <ListItem>Legal and professional advisors when required</ListItem>
                    <ListItem>Regulatory authorities when legally obligated</ListItem>
                  </UnorderedList>
                  <Text color="gray.700" fontSize="sm">
                    We ensure all third parties comply with strict data protection standards and use your information
                    only for specified purposes.
                  </Text>
                </VStack>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem id="section-6" border="1px" borderColor="gray.200" borderRadius="md" mb={4}>
              <h2>
                <AccordionButton py={4} _hover={{ bg: "blue.50" }}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md" color="blue.800">6. Your Privacy Rights</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={6} bg="gray.50">
                <VStack align="start" spacing={4}>
                  <Text color="gray.700">
                    <strong>[PLACEHOLDER]</strong> Under the Privacy Act 1988, you have the following rights:
                  </Text>
                  <UnorderedList spacing={2} color="gray.700">
                    <ListItem><strong>Access:</strong> Request access to your personal information</ListItem>
                    <ListItem><strong>Correction:</strong> Request correction of inaccurate or incomplete data</ListItem>
                    <ListItem><strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</ListItem>
                    <ListItem><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time</ListItem>
                    <ListItem><strong>Data portability:</strong> Request a copy of your data in a portable format</ListItem>
                    <ListItem><strong>Complaint:</strong> Lodge a complaint about our privacy practices</ListItem>
                  </UnorderedList>
                  <Text color="gray.700" fontSize="sm">
                    To exercise these rights, please contact our Privacy Officer using the details below. We will respond
                    to your request within 30 days.
                  </Text>
                </VStack>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem id="section-7" border="1px" borderColor="gray.200" borderRadius="md">
              <h2>
                <AccordionButton py={4} _hover={{ bg: "blue.50" }}>
                  <Box flex="1" textAlign="left">
                    <Heading size="md" color="blue.800">7. Contact Information</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={6} bg="gray.50">
                <VStack align="start" spacing={4}>
                  <Text color="gray.700">
                    <strong>[PLACEHOLDER]</strong> For privacy-related inquiries or to exercise your rights:
                  </Text>
                  <Box bg="white" p={4} borderRadius="md" w="full">
                    <Heading size="sm" mb={3}>Privacy Officer</Heading>
                    <VStack align="start" spacing={2} fontSize="sm">
                      <Text><strong>Email:</strong> privacy@onegroup.com.au</Text>
                      <Text><strong>Phone:</strong> 1300 XXX XXX</Text>
                      <Text><strong>Address:</strong> [Placeholder Address], Melbourne VIC 3000</Text>
                      <Text><strong>Office Hours:</strong> Monday - Friday, 9:00 AM - 5:00 PM AEST</Text>
                    </VStack>
                  </Box>
                  <Text color="gray.700" fontSize="sm">
                    If you are not satisfied with our response, you may contact the Office of the Australian Information
                    Commissioner (OAIC) at <Link color="blue.600" href="https://www.oaic.gov.au" isExternal>www.oaic.gov.au</Link>
                  </Text>
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>

          {/* Footer Note */}
          <Box bg="blue.50" p={6} borderRadius="md" mt={8}>
            <Text fontSize="sm" color="gray.700">
              <strong>Note:</strong> This Privacy Policy may be updated from time to time. We will notify you of any
              material changes by posting the updated policy on our website. Your continued use of our services after
              any changes constitutes acceptance of the updated policy.
            </Text>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default PrivacyPolicy;
